import { useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Sun, MoonStars, ArrowRight } from "@phosphor-icons/react";
import { useTheme } from "@/contexts/ThemeContext";
import { LodgyLogo } from "@/components/LodgyLogo";
import api, { formatApiError } from "@/lib/api";

export default function RegisterPage() {
  const { user, refreshUser } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", company_name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/dashboard" replace />;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password.length < 8) {
      setError("Lozinka mora imati barem 8 znakova.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/register", form);
      // The register endpoint sets auth cookies, so refresh user context
      await refreshUser?.();
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center bg-stone-900 p-4">
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage: `url(https://images.unsplash.com/photo-1565784623727-8cebf4712b2d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHwxfHxjcm9hdGlhJTIwYWRyaWF0aWMlMjBjb2FzdCUyMHZpbGxhfGVufDB8fHx8MTc4MjIxMjg5OHww&ixlib=rb-4.1.0&q=85)`,
        }}
      />
      <div className="absolute inset-0 bg-stone-950/70" />

      <button
        onClick={toggle}
        className="absolute right-5 top-5 z-10 rounded-full bg-white/10 p-2 text-white backdrop-blur hover:bg-white/20"
        aria-label="Toggle theme"
      >
        {theme === "dark" ? <Sun size={18} weight="duotone" /> : <MoonStars size={18} weight="duotone" />}
      </button>

      <div className="relative z-10 w-full max-w-md" data-testid="register-card">
        <div className="mb-6 flex flex-col items-center text-center text-white">
          <LodgyLogo size={44} className="mb-3" />
          <p className="mt-1 text-sm text-white/75">Kreirajte račun u par sekundi</p>
        </div>

        <form
          onSubmit={submit}
          className="space-y-4 rounded-2xl border border-white/10 bg-white/95 p-7 shadow-2xl backdrop-blur dark:bg-stone-900/95 dark:border-white/5"
        >
          <div>
            <label className="mb-1.5 block text-sm font-medium text-foreground">Vaše ime i prezime</label>
            <input
              type="text"
              required
              value={form.name}
              onChange={set("name")}
              className="w-full rounded-md border border-border bg-background px-3 py-2.5 text-sm text-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
              placeholder="Marko Marković"
              data-testid="register-name-input"
            />
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-medium text-foreground">Naziv objekta / firme</label>
            <input
              type="text"
              required
              value={form.company_name}
              onChange={set("company_name")}
              className="w-full rounded-md border border-border bg-background px-3 py-2.5 text-sm text-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
              placeholder="Apartmani Sunce"
              data-testid="register-company-input"
            />
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-medium text-foreground">Email</label>
            <input
              type="email"
              required
              value={form.email}
              onChange={set("email")}
              className="w-full rounded-md border border-border bg-background px-3 py-2.5 text-sm text-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
              placeholder="ime@primjer.hr"
              data-testid="register-email-input"
            />
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-medium text-foreground">Lozinka <span className="text-muted-foreground">(min. 8 znakova)</span></label>
            <input
              type="password"
              required
              minLength={8}
              value={form.password}
              onChange={set("password")}
              className="w-full rounded-md border border-border bg-background px-3 py-2.5 text-sm text-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
              placeholder="••••••••"
              data-testid="register-password-input"
            />
          </div>

          {error && (
            <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive" data-testid="register-error">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="group flex w-full items-center justify-center gap-2 rounded-md bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground transition-all hover:bg-primary/90 disabled:opacity-50"
            data-testid="register-submit-btn"
          >
            {loading ? (
              <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/70 border-t-transparent" />
            ) : (
              <>
                Kreiraj račun
                <ArrowRight size={16} className="transition-transform group-hover:translate-x-0.5" />
              </>
            )}
          </button>

          <p className="pt-2 text-center text-xs text-muted-foreground">
            Već imate račun?{" "}
            <Link to="/login" className="font-semibold text-primary hover:underline" data-testid="register-login-link">
              Prijavite se
            </Link>
          </p>
        </form>

        <p className="mt-6 text-center text-xs text-white/50">© 2026 Lodgy.co — Property Management</p>
      </div>
    </div>
  );
}
